import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
    DEFAULT_LANGUAGE,
    STORAGE_KEY,
    SUPPORTED_LANGUAGES,
    isSupported,
    resolveLanguage,
    translate,
} from './dictionary';

/**
 * Minimal key/value translation layer with an English fallback.
 *
 * Why this and not i18next: the product is a single Create React App bundle
 * with no translation tooling, and the first market that needs Spanish is one
 * funnel — public demo, then registration. A library would add a loader, a
 * plugin chain and a build step for two dictionaries. This is the smallest
 * thing that avoids the real risk, which is duplicating components per
 * language and letting the copies drift.
 *
 * The lookup and the resolution rules live in ./dictionary so they can be
 * tested without React; this file is the provider and the hook.
 */

export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, resolveLanguage, translate };

const readStoredLanguage = () => {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return isSupported(stored) ? stored : null;
    } catch (error) {
        // Private mode or blocked storage: the preference is simply not kept.
        return null;
    }
};

const writeStoredLanguage = (language) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, language);
    } catch (error) {
        /* ignore: a language choice is not worth failing a render for */
    }
};

const readBrowserLanguage = () => {
    const candidates = typeof navigator === 'undefined'
        ? []
        : [...(navigator.languages || []), navigator.language].filter(Boolean);

    for (const candidate of candidates) {
        const base = String(candidate).toLowerCase().split('-')[0];
        if (isSupported(base)) return base;
    }
    return null;
};

const I18nContext = createContext({
    language: DEFAULT_LANGUAGE,
    t: (key, vars) => translate(DEFAULT_LANGUAGE, key, vars),
    buildLanguageHref: () => '#',
});

export const I18nProvider = ({ children }) => {
    const location = useLocation();
    const language = useMemo(() => resolveLanguage({
        search: location.search,
        pathname: location.pathname,
        stored: readStoredLanguage(),
        browser: readBrowserLanguage(),
    }), [location.search, location.pathname]);

    useEffect(() => {
        // Screen readers and the browser's own translation prompt both read
        // this attribute; index.html can only ever declare one language.
        document.documentElement.setAttribute('lang', language);
        const requested = new URLSearchParams(location.search).get('lang');
        if (isSupported(requested)) writeStoredLanguage(requested);
    }, [language, location.search]);

    const t = useCallback((key, vars) => translate(language, key, vars), [language]);

    const buildLanguageHref = useCallback((nextLanguage) => {
        const params = new URLSearchParams(location.search);
        params.set('lang', nextLanguage);
        return `${location.pathname}?${params.toString()}`;
    }, [location.pathname, location.search]);

    const value = useMemo(
        () => ({ language, t, buildLanguageHref }),
        [language, t, buildLanguageHref]
    );

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useTranslation = () => useContext(I18nContext);

export default I18nContext;
